import {
  AcceptOrderPayload,
  BackendOrderArgs,
  OrderResponse,
  OrdersQuery,
  OrderStateKey,
  OrderStateResponse,
  PreCreateOrder,
  UpdateOrder,
  UpdateOrderPayload,
  OrderReviewPayload,
  ReviewsResponse,
  MerchantStats,
  OrderUpdate,
  CreateOrderResponse,
  MerchantStatsData,
} from "@sdk/shared/types/order.types";
import { protectedAxios } from "@shared/src/utils/axios";
import { handleServiceError } from "@shared/src/utils/error-handler";
import { mapNetworkToIcon } from "@sdk/utils/icons";
import {
  processMerchantStats,
  processReviews,
  processPreCountedReviews,
} from "@shared/src/utils/helpers/merchant-stats";

export interface OrderPayload {
  order: PreCreateOrder;
  adId: number;
  signature?: string | null;
  paymentConfig?: number;
}

interface OrdersResponse {
  data: OrderResponse[];
}

export const orderService = {
  createOrder: async (payload: OrderPayload) => {
    return handleServiceError(async () => {
      const { data } = await protectedAxios.post("/orders/order", payload);
      if (!data) {
        throw new Error("Failed to create order");
      }
      return data as CreateOrderResponse;
    });
  },
  getOrder: async (orderHash: string) => {
    return handleServiceError(async () => {
      const { data }: { data: OrderResponse } = await protectedAxios.get("/orders/order/" + orderHash);

      if (!data) {
        throw new Error("Failed to fetch order");
      }
      if (!data.id) {
        throw new Error("Order is empty");
      }

      // Process merchant stats if available
      const stats = data.merchantStats ? processMerchantStats(data.merchantStats) : null;
      const reviewStats = data.merchantReviews ? processPreCountedReviews(data.merchantReviews) : undefined;

      return {
        ...data,
        traderNetwork: mapNetworkToIcon(data.traderNetwork),
        merchantNetwork: mapNetworkToIcon(data.merchantNetwork),
        orderStats: {
          totalOrders: stats?.totalOrders || 0,
          completionRate: stats?.completionRate || "0",
          avgReleaseTime: stats?.avgReleaseTime || "N/A",
          avgPayTime: stats?.avgPayTime || "N/A",
          positiveRates: reviewStats?.positiveRate || 0,
          timeLimit: data.ad.timeLimit,
        },
      } satisfies OrderResponse;
    });
  },
  getOrderState: async (orderHash: string) => {
    return handleServiceError(async () => {
      const { data }: { data: OrderStateResponse } = await protectedAxios.get("/orders/order/" + orderHash + "/state");
      if (!data) {
        throw new Error("Failed to fetch order");
      }
      return data;
    });
  },
  getOrders: async (query: OrdersQuery) => {
    if (!query.orderBy) {
      query.orderBy = "createdAt";
    }
    return handleServiceError(async () => {
      const { data } = await protectedAxios.get<OrderResponse[]>("/orders", {
        params: query,
      });

      if (!data) {
        throw new Error("Failed to fetch orders");
      }

      return data.map(order => {
        const stats = order.merchantStats ? processMerchantStats(order.merchantStats) : null;
        const reviewStats = order.merchantReviews ? processPreCountedReviews(order.merchantReviews) : undefined;

        return {
          ...order,
          traderNetwork: mapNetworkToIcon(order.traderNetwork),
          merchantNetwork: mapNetworkToIcon(order.merchantNetwork),
          orderStats: {
            totalOrders: stats?.totalOrders || 0,
            completionRate: stats?.completionRate || "0",
            avgReleaseTime: stats?.avgReleaseTime || "N/A",
            avgPayTime: stats?.avgPayTime || "N/A",
            positiveRates: reviewStats?.positiveRate || 0,
            positiveRate: reviewStats?.positiveRate || 0,
            timeLimit: order.ad.timeLimit,
          },
        };
      });
    });
  },
  updateOrder: async (orderHash: string, payload: UpdateOrderPayload) => {
    return handleServiceError<OrderUpdate>(async () => {
      const { data } = await protectedAxios.post("orders/order/" + orderHash, payload);
      if (!data) {
        throw new Error("Failed to update order");
      }
      return data;
    });
  },
  acceptOrder: async (orderHash: string, payload: AcceptOrderPayload) => {
    return handleServiceError<OrderUpdate>(async () => {
      const { data } = await protectedAxios.post("orders/order/" + orderHash, payload);
      if (!data) {
        throw new Error("Failed to accept order");
      }
      return data;
    });
  },
  reviewOrder: async (orderHash: string, review: OrderReviewPayload) => {
    return handleServiceError<OrderUpdate>(async () => {
      const { data } = await protectedAxios.post(`/orders/order/${orderHash}/review`, review);
      if (!data) {
        throw new Error("Failed to submit review");
      }
      return data;
    });
  },
  getReviews: async (address: string) => {
    return handleServiceError(async () => {
      const { data } = await protectedAxios.get<ReviewsResponse>(`/orders/reviews/${address}`);
      if (!data) {
        throw new Error("Failed to fetch reviews");
      }
      return data;
    });
  },
  getMerchantStats: async (address: string): Promise<MerchantStatsData> => {
    const { data } = await protectedAxios.get<MerchantStats>(`/orders/stats/${address}`);

    if (!data) {
      throw new Error("Failed to fetch merchant stats");
    }

    // Extract review stats from numbered keys
    const reviewStats = Object.keys(data)
      .filter(key => !isNaN(Number(key)))
      .map(key => ({
        _count: data[key]._count,
        revieweeId: data[key].revieweeId,
        score: data[key].score,
      }));

    // Process reviews using the existing function
    const processedReviews = processReviews(reviewStats);

    // Extract merchant stats
    const { totalOrders, paid, released, accepted, address: merchantAddress } = data;

    const merchantStats: MerchantStats = {
      totalOrders,
      paid,
      released,
      accepted,
      address: merchantAddress,
      username: data.username || null,
      positiveRates: processedReviews.positiveRate,
    };
    return {
      merchantStats,
      reviews: {
        positive: processedReviews.positive,
        negative: processedReviews.negative,
        total: processedReviews.total,
      },
      reviewStats,
    };
  },
};
